import {
  amplitudeSendEvent,
  amplitudeSendScreenEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { SERVICE } from '@musinsa/packages-device-detection';

/** 레거시 mss-curator campaign 계열 analytics payload 이식 */

const SECTION_CAMPAIGN_DETAIL = 'brandsuggestion_campaign_detail';
const SECTION_COUPON_TITLE = 'brandsuggestion_campaign_coupontitle';
const SECTION_CAMPAIGN_LIST = 'campaign_list';
const SECTION_CURATOR_SUGGESTION_CREATE = 'curatorsuggestion_campaign_create';
const SCREEN_COLLABORATION = 'screen_curator_collaboration';
const SCREEN_CURATOR_SUGGESTION_SELECT_GOODS =
  'screen_curator_collaboration_curatorsuggestion_selectgoods';
const SCREEN_CURATOR_SUGGESTION_CAMPAIGN =
  'screen_curator_collaboration_curatorsuggestion_suggestioncampaign';

export type CampaignProposalStatus = 'WAITING' | 'APPROVED' | 'COMPLETED' | 'REJECTED';
export type CampaignProposalType = 'MY_PROPOSAL' | 'BRANDS_PROPOSAL';

export const CAMPAIGN_PROPOSAL_STATUS_LABELS: Record<CampaignProposalStatus, string> = {
  WAITING: '제안',
  APPROVED: '수락',
  COMPLETED: '완료',
  REJECTED: '거절',
};

export const CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB: Record<CampaignProposalStatus, string> = {
  WAITING: 'suggestion',
  APPROVED: 'acception',
  COMPLETED: 'completion',
  REJECTED: 'rejection',
};

export const CAMPAIGN_PROPOSAL_TYPE_LABELS: Record<CampaignProposalType, string> = {
  MY_PROPOSAL: '나의 협업 제안',
  BRANDS_PROPOSAL: '브랜드의 협업 제안',
};

export function isMusinsaService(service: string): boolean {
  return service === SERVICE.MUSINSA;
}

export function trackCampaignCollaborationScreenViewMusinsa(): void {
  amplitudeSendScreenEvent({
    event_name: SCREEN_COLLABORATION,
    from: getScreenName(),
  });
}

export function trackCampaignCollaborationTopBannerClickMusinsa(params: {
  title: string;
  appliedTab: string;
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: params.appliedTab,
    button_name: 'top_banner',
    component_name: 'button',
    event_name: 'clink_banner',
    from: SCREEN_COLLABORATION,
    name: params.title,
    section_name: 'collaboration_top_banner',
  });
}

export function trackCampaignCollaborationPlusClickMusinsa(): void {
  amplitudeSendEvent({
    from: SCREEN_COLLABORATION,
    event_name: 'click_button',
    section_name: SECTION_CAMPAIGN_LIST,
    button_name: 'suggestion_try',
  });
}

export function trackCampaignCollaborationTypeTabClickMusinsa(params: { tabTitle: string }): void {
  amplitudeSendEvent({
    from: SCREEN_COLLABORATION,
    section_name: 'suggestion_type_tab',
    button_name: 'suggestion_tab',
    event_name: 'click_tab',
    name: params.tabTitle,
  });
}

export function trackCampaignCollaborationStatusTabClickMusinsa(params: {
  tabTitle: string;
  appliedTab: string;
  type: 'curator' | 'brand';
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: params.appliedTab,
    button_name: 'suggestion_tab',
    component_name: 'tab',
    event_name: 'click_tab',
    from: SCREEN_COLLABORATION,
    name: params.tabTitle,
    section_name: 'suggestion_tab',
    type: params.type,
  });
}

export function trackBrandProposalCampaignClickMusinsa(params: {
  campaignTitle: string;
  appliedTab: string;
  suggestionNo: string;
  brandId: string;
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: params.appliedTab,
    brand_id: params.brandId,
    button_name: 'brandsuggestion_campaign',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });
}

export function trackBrandProposalCopyKeywordClickMusinsa(params: {
  campaignTitle: string;
  suggestionNo: string;
  brandId: string;
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: 'acception',
    brand_id: params.brandId,
    button_name: 'copy_couponkeyword',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });
}

export function trackMyProposalCampaignClickMusinsa(params: {
  campaignTitle: string;
  appliedTab: string;
  suggestionNo: string;
  brandId: string;
}): void {
  amplitudeSendEvent({
    applied_tab: params.appliedTab,
    brand_id: params.brandId,
    button_name: 'curatorsuggestion_campaign',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });
}

export function trackMyProposalDetailClickMusinsa(params: {
  campaignTitle: string;
  suggestionNo: string;
  brandId: string;
}): void {
  amplitudeSendEvent({
    applied_tab: 'acception',
    brand_id: params.brandId,
    button_name: 'curatorsuggestion_campaign_propsal',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });
}

// NOTE: Phase2 신규 — 브랜드 협업 제안 > 수락 탭 카드 하단의 두 액션 버튼.
// AMP 시트 4 (04-03) row 10/11 + GA4 2605 브랜드협업제안 시트 row 2/3.
const SECTION_BRAND_PROPOSAL_COUPON = 'curator_coupon';

export function trackBrandProposalCopyAppliedLinkClickMusinsa(params: {
  campaignTitle: string;
  suggestionNo: string;
  brandId: string;
  curatorId: string;
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: 'acception',
    brand_id: params.brandId,
    button_name: 'copy_link',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });

  pushDataLayer('click_content', {
    brand_id: params.brandId,
    content_name: params.campaignTitle,
    content_profile_id: params.curatorId,
    extra_info: 'copy_link',
    index: GTM_NOTSET_STRING,
    section_index: '2',
    section_name: SECTION_BRAND_PROPOSAL_COUPON,
  });
}

export function trackBrandProposalAppliedGoodsClickMusinsa(params: {
  campaignTitle: string;
  suggestionNo: string;
  brandId: string;
  curatorId: string;
}): void {
  amplitudeSendEvent({
    action_name: 'click',
    applied_tab: 'acception',
    brand_id: params.brandId,
    button_name: 'applied_goods',
    component_name: 'button',
    event_name: 'click_button',
    from: SCREEN_COLLABORATION,
    id: params.suggestionNo,
    name: params.campaignTitle,
    section_name: SECTION_CAMPAIGN_LIST,
  });

  pushDataLayer('click_content', {
    brand_id: params.brandId,
    content_name: params.campaignTitle,
    content_profile_id: params.curatorId,
    extra_info: 'applied_goods',
    index: GTM_NOTSET_STRING,
    section_index: '3',
    section_name: SECTION_BRAND_PROPOSAL_COUPON,
  });
}

export function trackCampaignSearchScreenViewMusinsa(): void {
  amplitudeSendScreenEvent({
    event_name: SCREEN_CURATOR_SUGGESTION_SELECT_GOODS,
    from: getScreenName(),
  });
}

export function trackCampaignSearchKeywordClickMusinsa(params: { keyword: string }): void {
  amplitudeSendEvent({
    action_name: 'click',
    component_name: 'search_button',
    event_name: 'click_search_button',
    from: getScreenName(),
    section_name: 'search_goods',
    button_name: 'goods_search',
    name: params.keyword,
  });

  pushDataLayer('click_button', {
    brand_id: GTM_NOTSET_STRING,
    button_id: 'goods_search',
    button_name: params.keyword,
    index: GTM_NOTSET_STRING,
    section_index: '1',
    section_name: 'search_goods',
  });
}

export function trackCampaignSearchBrandGoodsClickMusinsa(params: {
  brandId: string;
  id: string;
}): void {
  amplitudeSendEvent({
    brand_id: params.brandId,
    button_name: 'brandgoods',
    event_name: 'click_button',
    from: SCREEN_CURATOR_SUGGESTION_SELECT_GOODS,
    id: params.id,
    section_name: SECTION_CURATOR_SUGGESTION_CREATE,
    type: 'brand',
  });
}

export function trackCampaignSearchGoodsSelectTryClickMusinsa(params: { id: string }): void {
  amplitudeSendEvent({
    button_name: 'goods_select_try',
    event_name: 'click_button',
    from: SCREEN_CURATOR_SUGGESTION_SELECT_GOODS,
    id: params.id,
    section_name: SECTION_CURATOR_SUGGESTION_CREATE,
    type: 'goods',
  });
}

export function trackCampaignSuggestionActionClickMusinsa(params: {
  name: 'cancel' | 'suggestion';
  brandId: string;
  id: string;
}): void {
  amplitudeSendEvent({
    brand_id: params.brandId,
    button_name: 'curatorsuggestion_try',
    event_name: 'click_button',
    from: SCREEN_CURATOR_SUGGESTION_CAMPAIGN,
    id: params.id,
    name: params.name,
    section_name: SECTION_CURATOR_SUGGESTION_CREATE,
  });
}

export function trackCouponGuideScreenViewMusinsa(): void {
  amplitudeSendScreenEvent({
    event_name: 'screen_curator_collaboration_brandsuggestion_guide',
    from: getScreenName(),
  });
}

/** 제안 상세 화면 진입 (Amplitude screen) */
export function trackProposalDetailScreenViewMusinsa(): void {
  amplitudeSendScreenEvent({
    event_name: 'screen_curator_collaboration_brandsuggestion',
    from: getScreenName(),
  });
}

/** 쿠폰명 선택 화면 진입 (Amplitude screen) */
export function trackCouponNameScreenViewMusinsa(): void {
  amplitudeSendScreenEvent({
    event_name: 'screen_curator_collaboration_brandsuggestion_couponname',
    from: getScreenName(),
  });
}

/** 하단 수락 / 거절 버튼 — `name`: accept | reject */
export function trackCampaignProposalResponseClickMusinsa(params: {
  name: 'accept' | 'reject';
  campaignProposalId: number;
  brandId: string;
}): void {
  const id = String(params.campaignProposalId);
  const brand_id = params.brandId;

  amplitudeSendEvent({
    action_name: 'click',
    brand_id,
    button_name: 'brandsuggestion_campaign',
    component_name: 'button',
    event_name: 'click_button',
    from: 'screen_curator_collaboration',
    id,
    name: params.name,
    section_name: SECTION_CAMPAIGN_DETAIL,
  });
}

/** 거절 확인 다이얼로그 — 취소 / 거절하기 (`name`: cancel | reject) */
export function trackCampaignProposalRejectDialogClickMusinsa(params: {
  name: 'cancel' | 'reject';
  campaignProposalId: number;
  brandId: string;
}): void {
  const id = String(params.campaignProposalId);
  const brand_id = params.brandId;

  amplitudeSendEvent({
    action_name: 'click',
    brand_id,
    button_name: 'brandsuggestion_campaign_detail',
    component_name: 'button',
    event_name: 'click_button',
    from: 'screen_curator_collaboration',
    id,
    name: params.name,
    section_name: SECTION_CAMPAIGN_DETAIL,
  });
}

/** 쿠폰명 옵션 선택 — 레거시 `button_name` 끝 공백 유지, `name` 은 소문자 */
export function trackCouponNameOptionClickMusinsa(params: { couponNameType: string }): void {
  const name = params.couponNameType.toLowerCase();
  amplitudeSendEvent({
    action_name: 'click',
    button_name: 'select_couponname ',
    component_name: 'button',
    event_name: 'click_button',
    from: 'screen_curator_collaboration_brandsuggestion_couponname',
    name,
    section_name: SECTION_COUPON_TITLE,
  });
}

/** 쿠폰명 화면 완료 — `name` 은 소문자 (레거시 SelectCouponName) */
export function trackCouponNameCompleteClickMusinsa(params: { couponNameType: string }): void {
  const name = params.couponNameType.toLowerCase();
  amplitudeSendEvent({
    action_name: 'click',
    button_name: 'complete_couponname',
    component_name: 'button',
    event_name: 'click_button',
    from: 'screen_curator_collaboration_brandsuggestion_couponname',
    name,
    section_name: SECTION_COUPON_TITLE,
  });
}
